// «Загрузить базу»: таблица бизнесов (выставка, холодная база, выгрузка из
// другой CRM) → сопоставление колонок → проверка шлюзом (dryRun: что заведём,
// что дубль, что с ошибкой) → «Завести N заявок». Файл читается в браузере
// тем же парсером, что у импорта товаров в кабинете продавца (xlsx/csv).
import { CRM_IMPORT_FIELDS, CRM_LEAD_SOURCES, CRM_LEAD_SOURCE_LABELS, type CrmImportField, type CrmLeadImport, type CrmLeadImportResult, type CrmLeadSource } from "@osmix/contracts";
import { readSpreadsheet, SpreadsheetError, type SpreadsheetRow } from "@osmix/catalog-core/spreadsheet";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { FileSpreadsheet, Upload } from "lucide-react";
import { type DragEvent, useId, useMemo, useState } from "react";
import { Link } from "react-router";
import type { ApplicationAssignee } from "@/api";
import { useImportLeads } from "@/api/hooks/use-application-crm";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { errorText } from "./format";
import { Chip, FieldError, Spinner } from "./ui";
export const IMPORT_FIELD_LABELS: Record<CrmImportField, string> = {
  shopName: "Название магазина",
  contactName: "Контактное лицо",
  phone: "Телефон",
  email: "Почта",
  city: "Город",
  inn: "ИНН",
  categories: "Категории",
  website: "Сайт",
  instagram: "Instagram",
  vk: "ВКонтакте",
  telegram: "Telegram",
  wildberries: "Wildberries",
  ozon: "Ozon",
  comment: "Комментарий",
  tags: "Теги"
};

/**
 * Подсказки по заголовкам. Порядок важен: «Контактный телефон» — телефон, а не
 * контакт; «Telegram» — до телефона, иначе «тел…» заберёт его. `\b` с
 * кириллицей не работает — границы слов заданы явно.
 */
const HEADER_HINTS: [CrmImportField, RegExp][] = [["inn", /(^|[^а-яa-z])(инн|inn)([^а-яa-z]|$)/], ["email", /e-?mail|почт|мейл/], ["telegram", /telegram|телеграм|(^|[^а-яa-z])(tg|тг)([^а-яa-z]|$)/], ["instagram", /insta|инст/], ["vk", /вконтакт|(^|[^а-яa-z])(vk|вк)([^а-яa-z]|$)/], ["wildberries", /wildberr|вайлдб|(^|[^а-яa-z])(wb|вб)([^а-яa-z]|$)/], ["ozon", /ozon|озон/], ["website", /сайт|website|site|url|ссылк/], ["phone", /телеф|(^|[^а-яa-z])тел([^а-яa-z]|$)|phone|моб|whatsapp|ватсап/], ["contactName", /контакт|фио|имя|представ|contact|person/], ["shopName", /магазин|назван|бренд|компан|организац|shop|brand|company|store|продав|наименов/], ["city", /город|city|регион|населен/], ["categories", /категор|ниш|направлен|ассортимент|category/], ["tags", /тег|метк|tag/], ["comment", /коммент|примеч|заметк|comment|note|описан/]];
export const suggestField = (header: string): CrmImportField | null => {
  const text = header.trim().toLowerCase();
  if (!text) return null;
  return HEADER_HINTS.find(([, pattern]) => pattern.test(text))?.[0] ?? null;
};

/** Колонка → поле; каждое поле — один раз (первая подходящая колонка). */
export const suggestMapping = (headers: string[]): (CrmImportField | null)[] => {
  const used = new Set<CrmImportField>();
  return headers.map(header => {
    const field = suggestField(header);
    if (!field || used.has(field)) return null;
    used.add(field);
    return field;
  });
};

/** Строка заголовков: в первых десяти — та, где узнаётся больше всего полей (у баз бывает шапка-название). */
const detectHeaderRow = (rows: SpreadsheetRow[]): number => {
  let best = 0;
  let bestScore = 0;
  rows.slice(0, 10).forEach((row, index) => {
    const score = row.cells.filter(cell => suggestField(cell)).length;
    if (score > bestScore) {
      best = index;
      bestScore = score;
    }
  });
  return best;
};
const MAX_ROWS = 5000;
const MAX_COLUMNS = 100;
type Sheet = {
  fileName: string;
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
};
type Step = "file" | "mapping" | "check" | "done";
const STATUS_LABEL: Record<CrmLeadImportResult["rows"][number]["status"], string> = {
  create: "Будет заведена",
  created: "Заведена",
  duplicate: "Дубль",
  invalid: "Ошибка"
};
const STATUS_TONE: Record<CrmLeadImportResult["rows"][number]["status"], string> = {
  create: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  created: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  duplicate: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500",
  invalid: "border-destructive/40 bg-destructive/10 text-destructive"
};
export function ImportLeadsDialog({
  open,
  onOpenChange,
  assignees
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignees: ApplicationAssignee[];
}) {
  const inputId = useId();
  const importLeads = useImportLeads();
  const [step, setStep] = useState<Step>("file");
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [mapping, setMapping] = useState<(CrmImportField | null)[]>([]);
  const [source, setSource] = useState<CrmLeadSource>("cold_base");
  const [sourceDetail, setSourceDetail] = useState("");
  const [tags, setTags] = useState("");
  const [assignee, setAssignee] = useState("");
  const [reading, setReading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrmLeadImportResult | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const reset = () => {
    setStep("file");
    setSheet(null);
    setMapping([]);
    setSource("cold_base");
    setSourceDetail("");
    setTags("");
    setAssignee("");
    setError(null);
    setResult(null);
    setOnlyProblems(false);
    setTruncated(false);
  };
  const close = (next: boolean) => {
    if (!next && importLeads.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  };
  const openFile = async (file: File) => {
    setError(null);
    setReading(true);
    try {
      const parsed = await readSpreadsheet(new Uint8Array(await file.arrayBuffer()), file.name);
      const headerIndex = detectHeaderRow(parsed.rows);
      const headerRow = parsed.rows[headerIndex];
      const body = parsed.rows.slice(headerIndex + 1);
      if (!headerRow || body.length === 0) throw new SpreadsheetError("empty", "Под заголовками нет строк");
      const width = Math.min(MAX_COLUMNS, Math.max(headerRow.cells.length, ...body.map(row => row.cells.length)));
      const headers = Array.from({
        length: width
      }, (_, i) => headerRow.cells[i]?.slice(0, 300) || `Колонка ${i + 1}`);
      const kept = body.slice(0, MAX_ROWS);
      setTruncated(body.length > MAX_ROWS);
      setSheet({
        fileName: file.name,
        headers,
        rows: kept.map(row_0 => Array.from({
          length: width
        }, (__0, i_0) => (row_0.cells[i_0] ?? "").slice(0, 5000))),
        rowNumbers: kept.map(row_1 => row_1.n)
      });
      setMapping(suggestMapping(headers));
      setSourceDetail(file.name.replace(/\.[^.]+$/, "").slice(0, 300));
      setStep("mapping");
    } catch (e) {
      setError(e instanceof SpreadsheetError ? e.message : `Не удалось прочитать файл: ${errorText(e)}`);
    } finally {
      setReading(false);
    }
  };
  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragOver(false);
    const file_0 = event.dataTransfer.files[0];
    if (file_0) void openFile(file_0);
  };
  const hasName = mapping.includes("shopName");
  const hasPhone = mapping.includes("phone");
  const request = (dryRun: boolean): CrmLeadImport | null => {
    if (!sheet) return null;
    return {
      headers: sheet.headers,
      rows: sheet.rows,
      mapping,
      source,
      sourceDetail: sourceDetail.trim() || undefined,
      tags: tags.split(/[,;]/).map(tag => tag.trim()).filter(Boolean),
      ...(assignee === "none" ? {
        assigneeId: null
      } : assignee ? {
        assigneeId: assignee
      } : {}),
      dryRun
    };
  };
  const run = async (dryRun_0: boolean) => {
    const input = request(dryRun_0);
    if (!input) return;
    setError(null);
    try {
      const next_0 = await importLeads.mutateAsync(input);
      setResult(next_0);
      setOnlyProblems(false);
      setStep(dryRun_0 ? "check" : "done");
    } catch (e_0) {
      setError(errorText(e_0));
    }
  };
  const samples = useMemo(() => sheet ? sheet.headers.map((__1, column) => sheet.rows.map(row_2 => row_2[column] ?? "").filter(Boolean).slice(0, 2)) : [], [sheet]);

  /** Номер строки в файле (как в Excel): шлюз считает строки данных с единицы без заголовка. */
  const fileRow = (row_3: number) => sheet?.rowNumbers[row_3 - 1] ?? row_3;

  // При проверке шлюз кладёт в `created` «будет заведено», после записи — сколько завёл.
  const toCreate = result?.created ?? 0;
  const rows = result ? onlyProblems ? result.rows.filter(row_4 => row_4.status === "duplicate" || row_4.status === "invalid") : result.rows : [];
  return <Dialog open={open} onOpenChange={close}>
      <DialogContent className="flex max-h-[92dvh] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-6 pt-6 pb-4">
          <DialogTitle>Загрузить базу заявок</DialogTitle>
          <DialogDescription>
            {step === "file" && "Таблица xlsx или csv: одна строка — один бизнес. Обязательны название и телефон."}
            {step === "mapping" && sheet && `${sheet.fileName} · ${sheet.rows.length} строк. Сопоставьте колонки с полями заявки.`}
            {step === "check" && "Проверка без записи: что заведём, что уже есть, где ошибки."}
            {step === "done" && "Готово."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {step === "file" && <div className="flex flex-col gap-3">
              <label htmlFor={inputId} onDragOver={event_0 => {
            event_0.preventDefault();
            setDragOver(true);
          }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={cn("flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center text-sm transition-colors hover:bg-muted/40", dragOver && "border-primary bg-primary/5")}>
                {reading ? <Spinner /> : <FileSpreadsheet className="size-6 text-muted-foreground" />}
                <span>
                  Перетащите файл или <span className="text-primary">выберите</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  xlsx или csv, до {MAX_ROWS.toLocaleString("ru-RU")} строк. Старый .xls — пересохраните в xlsx.
                </span>
              </label>
              <input id={inputId} type="file" accept=".xlsx,.csv,.tsv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="sr-only" onChange={event_1 => {
            const file_1 = event_1.target.files?.[0];
            event_1.target.value = "";
            if (file_1) void openFile(file_1);
          }} />
            </div>}

          {step === "mapping" && sheet && <div className="flex flex-col gap-4">
              {truncated && <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-400">
                  В файле больше {MAX_ROWS.toLocaleString("ru-RU")} строк — загрузим первые {MAX_ROWS.toLocaleString("ru-RU")}, остальное
                  отдельным файлом.
                </p>}
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Колонка в файле</TableHead>
                      <TableHead className="hidden sm:table-cell">Пример</TableHead>
                      <TableHead>Поле заявки</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sheet.headers.map((header, column_0) => <TableRow key={`${column_0}-${header}`}>
                        <TableCell className="max-w-40 truncate font-medium" title={header}>
                          {header}
                          <span className="block truncate text-xs font-normal text-muted-foreground sm:hidden">
                            {samples[column_0]?.join(" · ")}
                          </span>
                        </TableCell>
                        <TableCell className="hidden max-w-56 truncate text-xs text-muted-foreground sm:table-cell">
                          {samples[column_0]?.join(" · ") || "—"}
                        </TableCell>
                        <TableCell>
                          <Select aria-label={`Поле для колонки «${header}»`} className="h-8 w-44" value={mapping[column_0] ?? ""} onChange={e_1 => setMapping(current => current.map((value, i_1) => i_1 === column_0 ? (e_1.target.value || null) as CrmImportField | null : value))}>
                            <option value="">Не загружать</option>
                            {CRM_IMPORT_FIELDS.map(field => <option key={field} value={field}>
                                {IMPORT_FIELD_LABELS[field]}
                              </option>)}
                          </Select>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Источник для всех строк</span>
                  <Select className="w-full" value={source} onChange={e_2 => setSource(e_2.target.value as CrmLeadSource)}>
                    {CRM_LEAD_SOURCES.filter(key => key !== "landing").map(key_0 => <option key={key_0} value={key_0}>
                        {CRM_LEAD_SOURCE_LABELS[key_0]}
                      </option>)}
                  </Select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Уточнение</span>
                  <Input value={sourceDetail} maxLength={300} onChange={e_3 => setSourceDetail(e_3.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Теги для всех строк</span>
                  <Input placeholder="через запятую" value={tags} onChange={e_4 => setTags(e_4.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Ответственный</span>
                  <Select className="w-full" value={assignee} onChange={e_5 => setAssignee(e_5.target.value)}>
                    <option value="">Я</option>
                    {assignees.map(item => <option key={item.id} value={item.id}>
                        {item.name}
                      </option>)}
                    <option value="none">Без ответственного</option>
                  </Select>
                </label>
              </div>
              {(!hasName || !hasPhone) && <p className="text-xs text-destructive">
                  Укажите колонки {[!hasName && "«Название магазина»", !hasPhone && "«Телефон»"].filter(Boolean).join(" и ")} —
                  без них заявку не завести.
                </p>}
            </div>}

          {(step === "check" || step === "done") && result && <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2" data-testid="crm-import-summary">
                <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-700 dark:text-emerald-400">
                  {result.dryRun ? "Будет заведено" : "Заведено"} <b className="tabular-nums">{toCreate}</b>
                </span>
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-sm text-amber-700 dark:text-amber-500">
                  Дубли <b className="tabular-nums">{result.duplicates}</b>
                </span>
                <span className="rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1 text-sm text-destructive">
                  Ошибки <b className="tabular-nums">{result.invalid}</b>
                </span>
                <span className="rounded-full border px-3 py-1 text-sm text-muted-foreground">
                  Всего строк <b className="tabular-nums text-foreground">{result.total}</b>
                </span>
              </div>
              {result.duplicates + result.invalid > 0 && <div className="flex gap-1.5">
                  <Chip active={!onlyProblems} className="px-2.5 text-xs" onClick={() => setOnlyProblems(false)}>
                    Все строки
                  </Chip>
                  <Chip active={onlyProblems} className="px-2.5 text-xs" onClick={() => setOnlyProblems(true)}>
                    Только дубли и ошибки
                  </Chip>
                </div>}
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Строка</TableHead>
                      <TableHead>Магазин</TableHead>
                      <TableHead>Итог</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.slice(0, 300).map(row_5 => <TableRow key={`${row_5.row}-${row_5.status}`}>
                        <TableCell className="tabular-nums text-muted-foreground">{fileRow(row_5.row)}</TableCell>
                        <TableCell className="max-w-56 truncate">
                          {row_5.applicationId && row_5.status !== "invalid" ? <Link to={`/applications/${row_5.applicationId}`} target="_blank" className="hover:underline">
                              {row_5.shopName || "—"}
                            </Link> : row_5.shopName || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("font-normal", STATUS_TONE[row_5.status])}>
                            {STATUS_LABEL[row_5.status]}
                          </Badge>
                          {row_5.message && <span className="ml-2 text-xs whitespace-normal text-muted-foreground">{row_5.message}</span>}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
              {rows.length > 300 && <p className="text-xs text-muted-foreground">Показаны первые 300 строк из {rows.length}.</p>}
            </div>}
        </div>

        <div className="flex flex-col gap-2 border-t px-6 py-4">
          <FieldError>{error}</FieldError>
          <div className="flex flex-wrap justify-end gap-2">
            {step === "mapping" && <>
                <Button variant="ghost" onClick={reset} disabled={importLeads.isPending}>
                  Другой файл
                </Button>
                <Button disabled={!hasName || !hasPhone || importLeads.isPending} onClick={() => void run(true)}>
                  {importLeads.isPending && <Spinner />}
                  Проверить
                </Button>
              </>}
            {step === "check" && <>
                <Button variant="ghost" disabled={importLeads.isPending} onClick={() => setStep("mapping")}>
                  Назад к колонкам
                </Button>
                <Button disabled={toCreate === 0 || importLeads.isPending} onClick={() => void run(false)}>
                  {importLeads.isPending ? <Spinner /> : <Upload className="size-4" />}
                  Завести {toCreate} {toCreate % 10 === 1 && toCreate % 100 !== 11 ? "заявку" : toCreate % 10 >= 2 && toCreate % 10 <= 4 && (toCreate % 100 < 12 || toCreate % 100 > 14) ? "заявки" : "заявок"}
                </Button>
              </>}
            {(step === "file" || step === "done") && <Button variant={step === "done" ? "default" : "outline"} onClick={() => close(false)}>
                {step === "done" ? "Готово" : "Отмена"}
              </Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}